import {
  type JSX,
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { type WebSession, apiGet, currentSession, onSessionChange, refresh, signOut } from './client';

/**
 * Who is signed in, for the website's pages.
 *
 * ## Lazy on purpose
 *
 * The provider wraps the whole app, but it asks the server nothing until a page
 * that cares asks it (`useSession`). The homepage never does, so Home makes no
 * session request and renders exactly as it did — the freeze holds — while every
 * website page shares one session that survives client-side navigation.
 */

export type SessionStatus = 'idle' | 'loading' | 'signedIn' | 'signedOut' | 'unreachable';

export interface SessionUser {
  readonly email: string;
  readonly personId: string;
  /** The person's own name, when they have given one. Never invented. */
  readonly displayName?: string;
}

export interface SessionState {
  readonly status: SessionStatus;
  readonly user?: SessionUser;
  /** Ends the session on the server. */
  signOut(): Promise<void>;
  /** Asks again after `unreachable`. */
  retry(): void;
  /** The person changed their own name (Account › Personal details), so the header follows at once. */
  nameChanged(displayName: string | undefined): void;
}

const SessionContext = createContext<(SessionState & { ensureLoaded(): void }) | undefined>(undefined);

function toUser(session: WebSession, displayName?: string): SessionUser {
  return {
    email: session.account.email,
    personId: session.account.personId,
    ...(displayName !== undefined && displayName.trim().length > 0
      ? { displayName: displayName.trim() }
      : {}),
  };
}

export function SessionProvider({ children }: { readonly children: ReactNode }): JSX.Element {
  const [status, setStatus] = useState<SessionStatus>(() =>
    currentSession() === undefined ? 'idle' : 'signedIn',
  );
  const [user, setUser] = useState<SessionUser | undefined>(() => {
    const session = currentSession();
    return session === undefined ? undefined : toUser(session);
  });
  const started = useRef(false);
  const profileFor = useRef<string | undefined>(undefined);

  // Follow the session wherever it changes: sign-in on /login, a refresh, a
  // sign-out from the header, or a refresh that found the session ended.
  useEffect(
    () =>
      onSessionChange((session) => {
        if (session === undefined) {
          profileFor.current = undefined;
          setUser(undefined);
          setStatus('signedOut');
          return;
        }
        setUser((previous) =>
          toUser(session, previous?.personId === session.account.personId ? previous.displayName : undefined),
        );
        setStatus('signedIn');
      }),
    [],
  );

  // The person's name, once per person. Best effort: without it the header
  // shows the email address, which is true; it never makes a name up.
  useEffect(() => {
    if (status !== 'signedIn' || user === undefined || profileFor.current === user.personId) return;
    profileFor.current = user.personId;
    apiGet<{ displayName?: string }>('/person/me')
      .then((profile) => {
        const session = currentSession();
        if (session !== undefined && session.account.personId === user.personId) {
          setUser(toUser(session, profile.displayName));
        }
      })
      .catch(() => undefined);
  }, [status, user]);

  const load = useCallback(() => {
    setStatus('loading');
    refresh()
      .then((session) => setStatus(session === undefined ? 'signedOut' : 'signedIn'))
      .catch(() => setStatus('unreachable'));
  }, []);

  const ensureLoaded = useCallback(() => {
    if (started.current) return;
    started.current = true;
    if (currentSession() !== undefined) {
      setStatus('signedIn');
      return;
    }
    load();
  }, [load]);

  const nameChanged = useCallback((displayName: string | undefined) => {
    const session = currentSession();
    if (session === undefined) return;
    setUser(toUser(session, displayName));
  }, []);

  const value = useMemo(
    () => ({
      status,
      ...(user !== undefined ? { user } : {}),
      ensureLoaded,
      signOut: () => signOut(),
      retry: load,
      nameChanged,
    }),
    [status, user, ensureLoaded, load, nameChanged],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

/** The session, starting the first check if nobody has yet. */
export function useSession(): SessionState {
  const value = useContext(SessionContext);
  if (value === undefined) throw new Error('useSession must be used inside a SessionProvider.');

  const { ensureLoaded } = value;
  useEffect(() => ensureLoaded(), [ensureLoaded]);

  return value;
}

/** Two letters for the avatar: from the person's own name, else their email. */
export function initialsOf(user: SessionUser): string {
  const source = user.displayName ?? user.email.split('@')[0] ?? '';
  const words = source.split(/[\s._-]+/).filter((word) => word.length > 0);
  const letters = words.length >= 2 ? `${words[0]?.[0] ?? ''}${words[1]?.[0] ?? ''}` : source.slice(0, 2);
  return letters.toUpperCase();
}
